// Reactおよび関連ライブラリをインポート
import React, { useState, useEffect, useRef, useCallback } from "react";
import apiClient from "../../../api/axiosConfig";
import { useLocation } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import { useRoom } from '../../../contexts/RoomContext';
import { getApiBaseUrl } from '../../../shared/config/urls';
import { getBaseUrl } from "../../../config/urls";

// --- アセット（画像ファイル）をインポート ---
import backGroundScreen from '../../../img/background_screen.png';
import heartOutline from '../../../img/finale-heart.svg';
import heartMaskForComposite from '../../../img/heart_mask5.svg';
import fallbackImage from '../../../img/back_ground2.png';

// --- 定数定義 ---
const FADE_ANIMATION_DURATION_MS = 1500;
const IMAGE_DISPLAY_DURATION_MS = 3000;
const HEART_IMAGE_COUNT = 52;
const HEART_FADE_DURATION_MS = 1000;
const HEART_ANIMATION_DELAY_MS = 0; // 順次取得に合わせて0に設定
const FADEOUT_START_DELAY_MS = 3000;


// --- 型定義 ---
type LocationState = {
    roomid: string;
};
type HeartPosition = {
    x: number;
    y: number;
    scale: number;
};
type ScreenState = 'background' | 'heartAnimation';


// --- ヘルパー関数 ---
const getImageDimensions = (base64: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            resolve({ width: img.width, height: img.height });
        };
        img.onerror = (err) => {
            console.error("画像データの解析に失敗しました。", err);
            reject(new Error("Image load error"));
        };
        img.src = `data:image/png;base64,${base64}`;
    });
};

// --- heartPositions データ ---
const heartPositions: HeartPosition[] = [
    { x: 22, y: 4, scale: 1.6 }, { x: 30, y: 4, scale: 1.6 }, { x: 38, y: 5, scale: 1.6 },
    { x: 65, y: 5, scale: 1.6 }, { x: 73, y: 4, scale: 1.6 }, { x: 81, y: 4, scale: 1.6 },
    { x: 11, y: 13, scale: 1.8 }, { x: 21, y: 13, scale: 2.2 }, { x: 32, y: 13, scale: 2.2 },
    { x: 42, y: 14, scale: 2.0 }, { x: 51.5, y: 14, scale: 1.8 }, { x: 61, y: 14, scale: 2.0 },
    { x: 71, y: 13, scale: 2.2 }, { x: 82, y: 13, scale: 2.2 }, { x: 92, y: 13, scale: 1.8 },
    { x: 9, y: 20, scale: 1.4 }, { x: 9, y: 26, scale: 1.6 }, { x: 18, y: 24, scale: 2.3 },
    { x: 29, y: 24, scale: 2.3 }, { x: 40, y: 24, scale: 2.3 }, { x: 51, y: 24, scale: 2.3 },
    { x: 62, y: 24, scale: 2.3 }, { x: 73, y: 24, scale: 2.3 }, { x: 84, y: 24, scale: 2.3 },
    { x: 93, y: 20, scale: 1.4 }, { x: 93.5, y: 26, scale: 1.6 },
    { x: 9, y: 33, scale: 1.4 }, { x: 9.5, y: 39, scale: 1.0 }, { x: 18, y: 36, scale: 2.5 },
    { x: 29, y: 36, scale: 2.5 }, { x: 40, y: 36, scale: 2.5 }, { x: 51, y: 36, scale: 2.5 },
    { x: 62, y: 36, scale: 2.5 }, { x: 73, y: 36, scale: 2.5 }, { x: 84, y: 36, scale: 2.5 },
    { x: 93.5, y: 33, scale: 1.4 }, { x: 92.5, y: 39, scale: 1.0 },
    { x: 18, y: 47, scale: 2.0 }, { x: 29, y: 48, scale: 2.5 }, { x: 40, y: 48, scale: 2.5 },
    { x: 51, y: 48, scale: 2.5 }, { x: 62, y: 48, scale: 2.5 }, { x: 73, y: 48, scale: 2.5 },
    { x: 84, y: 47, scale: 2.0 },
    { x: 29, y: 59, scale: 2.0 }, { x: 40, y: 60, scale: 2.5 }, { x: 51, y: 60, scale: 2.5 },
    { x: 62, y: 60, scale: 2.5 }, { x: 73, y: 59, scale: 2.0 },
    { x: 41, y: 69, scale: 1.5 }, { x: 51, y: 72, scale: 2.5 }, { x: 61, y: 69, scale: 1.5 },
];

// --- スタイル定義 ---
const fadeIn = keyframes` from { opacity: 0; } to { opacity: 1; }`;
const fadeOut = keyframes` from { opacity: 1; } to { opacity: 0; }`;
const BackgroundContainer = styled.div`
    width: 100vw; height: 100vh; background-image: url(${backGroundScreen});
    background-size: cover; background-position: center; display: flex;
    justify-content: center; align-items: center; color: white;
    font-size: 2em; text-shadow: 2px 2px 4px rgba(0,0,0,0.7); overflow: hidden;
`;
const ImageContainer = styled.div<{ $isFadingOut: boolean }>`
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    background-color: transparent; display: flex; justify-content: center;
    align-items: center;
    animation: ${props => props.$isFadingOut
        ? css`${fadeOut} ${FADE_ANIMATION_DURATION_MS / 1000}s ease-in-out forwards`
        : css`${fadeIn} ${FADE_ANIMATION_DURATION_MS / 1000}s ease-in-out forwards`
    };
    img { width: 100%; height: 100%; object-fit: contain; }
`;
const LoadingOverlay = styled.div`
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    display: flex; flex-direction: column; justify-content: center;
    align-items: center; z-index: 10;
    .loading-spinner {
        border: 8px solid rgba(255, 255, 255, 0.3); border-top: 8px solid white;
        border-radius: 50%; width: 60px; height: 60px;
        animation: spin 1s linear infinite;
    }
    p { margin-top: 20px; }
    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;
const FrameWrapper = styled.div`
    position: relative; width: 108vmin; height: 102vmin;
    display: flex; justify-content: center; align-items: center;
`;
const HeartContainer = styled.div`
    position: relative; width: 100%; height: 100%;
    transform: translateX(-1.1vw) translateY(11vh); 
`;
const HeartOverlay = styled.img<{ $fadeOut: boolean }>`
    position: absolute; top: 0; left: 0; width: 100%; height: 100%;
    pointer-events: none; opacity: 1;
    transition: opacity ${HEART_FADE_DURATION_MS / 1000}s ease-out;
    z-index: 1;
    ${props => props.$fadeOut && css` animation: ${fadeOut} ${HEART_FADE_DURATION_MS / 1000}s ease-out forwards; `}
`;
const HeartImageWrapper = styled.div<{ $x: number; $y: number; $scale: number }>`
    position: absolute; left: ${props => props.$x}%; top: ${props => props.$y}%;
    transform: translate(-50%, -50%) scale(${props => props.$scale});
    width: 5%; aspect-ratio: 1 / 1;
`;
const HeartImage = styled.img<{ $delay: number, $fadeOut: boolean }>`
    width: 100%; height: 100%; object-fit: cover; border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3); opacity: 0; z-index: 2;
    animation: ${props => props.$fadeOut
        ? css`${fadeOut} ${HEART_FADE_DURATION_MS / 1000}s ease-out forwards`
        : css`${fadeIn} ${HEART_FADE_DURATION_MS / 1000}s ease-out forwards`
    };
    animation-delay: ${props => props.$fadeOut ? '0s' : `${props.$delay}s`};
`;
const CompositeImage = styled.img`
    max-width: 130vmin; max-height: 130vmin; object-fit: contain;
    opacity: 0; animation: ${fadeIn} 1.5s ease-out forwards;
`;


// --- コンポーネント本体 ---
const HeartAnimationScreen: React.FC = () => {
    // --- 共通のStateとHooks ---
    const location = useLocation();
    const { roomId: contextRoomId, setRoomId } = useRoom();
    const [localRoomId, setLocalRoomId] = useState<string | null>(null);
    const isMounted = useRef(true);

    // --- BackgroundScreen関連のState ---
    const [imageUrl, setImageUrl] = useState<string>("");
    const imageUrlRef = useRef(imageUrl);
    useEffect(() => {
        imageUrlRef.current = imageUrl;
    }, [imageUrl]);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const isLoading = imageUrl === "" && !!localRoomId;

    // --- HeartAnimationScreen関連のState ---
    const [heartImages, setHeartImages] = useState<(string | null)[]>(Array(HEART_IMAGE_COUNT).fill(null));
    const [isFetching, setIsFetching] = useState(false);
    const [startFadeOut, setStartFadeOut] = useState(false);
    const [animationComplete, setAnimationComplete] = useState(false);
    const [compositeImage, setCompositeImage] = useState<string | null>(null);
    const fetchedImagesRef = useRef<string[]>([]);
    const apiCallCountRef = useRef(0);
    const [currentScreen, setCurrentScreen] = useState<ScreenState>('background');

    // --- ヘルパー関数 ---
    const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    // --- API URL ---
    const urlBase = getBaseUrl();
    const urlNextImage = `${urlBase}/api/picture/next`;

    // --- 副作用 (useEffect) ---

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    // RoomIDを特定して設定する
    useEffect(() => {
        const locationState = location.state as LocationState;
        let foundRoomId: string | null = null;

        if (locationState?.roomid) {
            foundRoomId = locationState.roomid;
        } else if (contextRoomId) {
            foundRoomId = contextRoomId;
        } else {
            const sessionRoomId = sessionStorage.getItem("roomId");
            const localStoredRoomId = localStorage.getItem("currentRoomId");
            foundRoomId = sessionRoomId || localStoredRoomId;
        }

        if (!foundRoomId && process.env.NODE_ENV === 'development') {
            foundRoomId = '9ae63ec6-a832-4e41-be06-6e19336651cc';
        }

        if (foundRoomId) {
            if (foundRoomId !== localRoomId) setLocalRoomId(foundRoomId);
            if (foundRoomId !== contextRoomId) setRoomId(foundRoomId);
        }
    }, [location.state, contextRoomId, setRoomId, localRoomId]);

    // --- BackgroundScreen関連の関数 ---
    const fetchAndSetImage = useCallback(async (): Promise<void> => {
        if (!isMounted.current || !localRoomId) return;

        let landscapeImageFound = false;
        let newImageUrl = "";
        let attempt = 0;

        while (!landscapeImageFound && isMounted.current) {
            try {
                attempt++;
                const urlWithCacheBuster = `${urlNextImage}?roomId=${localRoomId}&_=${new Date().getTime()}`;
                apiCallCountRef.current++;
                const response = await apiClient.get<{ ret: string, msg: string, image: string }>(urlWithCacheBuster, { timeout: 15000 });
                const responseData = response.data;

                if (responseData && responseData.ret === 'true' && responseData.image) {
                    const base64 = responseData.image;
                    const dimensions = await getImageDimensions(base64);
                    if (dimensions.width > dimensions.height) {
                        newImageUrl = `data:image/png;base64,${base64}`;
                        landscapeImageFound = true;
                    }
                }
            } catch (error) {
                console.error("画像取得中にAPIエラーが発生しました:", error);
            }

            if (!landscapeImageFound && isMounted.current) {
                await wait(1000);
            }
        }

        if (landscapeImageFound && isMounted.current) {
            if (imageUrlRef.current) {
                setIsFadingOut(true);
                await wait(FADE_ANIMATION_DURATION_MS);
                if (!isMounted.current) return;
            }
            setIsFadingOut(false);
            setImageUrl(newImageUrl);
            await wait(FADE_ANIMATION_DURATION_MS);
        }
    }, [localRoomId, urlNextImage]);
    
    // --- HeartAnimationScreen関連の関数 ---
    const createCompositeImage = useCallback((imageSrc: string, maskSrc: string): void => {
        const mainImage = new Image();
        const maskImage = new Image();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        mainImage.crossOrigin = "Anonymous";
        Promise.all([
            new Promise(resolve => { mainImage.onload = resolve; }),
            new Promise(resolve => { maskImage.onload = resolve; })
        ]).then(() => {
            if (!ctx) return;
            canvas.width = maskImage.naturalWidth * 6.0;
            canvas.height = maskImage.naturalHeight * 6.0;
            ctx.drawImage(maskImage, 0, 0, canvas.width, canvas.height);
            ctx.globalCompositeOperation = 'source-in';
            const canvasAspect = canvas.width / canvas.height;
            const imageAspect = mainImage.naturalWidth / mainImage.naturalHeight;
            let sx, sy, sWidth, sHeight;
            if (imageAspect > canvasAspect) {
                sHeight = mainImage.naturalHeight;
                sWidth = sHeight * canvasAspect;
            } else {
                sWidth = mainImage.naturalWidth;
                sHeight = sWidth / canvasAspect;
            }
            const zoomFactor = 3.0;
            sWidth *= zoomFactor;
            sHeight *= zoomFactor;
            if (sWidth > mainImage.naturalWidth) {
                const ratio = mainImage.naturalWidth / sWidth;
                sWidth = mainImage.naturalWidth;
                sHeight *= ratio;
            }
            if (sHeight > mainImage.naturalHeight) {
                const ratio = mainImage.naturalHeight / sHeight;
                sHeight = mainImage.naturalHeight;
                sWidth *= ratio;
            }
            sx = (mainImage.naturalWidth - sWidth) / 2;
            sy = (mainImage.naturalHeight - sHeight) / 2;
            ctx.drawImage(mainImage, sx, sy, sWidth, sHeight, 0, 0, canvas.width, canvas.height);
            setCompositeImage(canvas.toDataURL('image/png'));
        }).catch(error => {
            console.error("画像読み込みエラー:", error);
        });
        mainImage.src = imageSrc;
        maskImage.src = maskSrc;
    }, []);

    // 1. 画面切り替え（Enterキー監視）
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter' && currentScreen === 'background') {
                setCurrentScreen('heartAnimation');
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentScreen]);

    // 2. BackgroundScreenの画像取得ロジック
    useEffect(() => {
        if (currentScreen !== 'background' || !localRoomId) return;

        let timerId: NodeJS.Timeout;

        const runCycle = async () => {
            if (!isMounted.current) return;
            await fetchAndSetImage();
            if (isMounted.current) {
                timerId = setTimeout(runCycle, IMAGE_DISPLAY_DURATION_MS);
            }
        };

        runCycle();

        return () => {
            clearTimeout(timerId);
        };
    }, [currentScreen, localRoomId, fetchAndSetImage]);

    // 3. HeartAnimationScreenのメインロジック
    useEffect(() => {
        if (currentScreen !== 'heartAnimation' || !localRoomId) {
            return;
        }

        const fetchImageOrThrow = async (index: number): Promise<string> => {
            if (!isMounted.current) throw new Error("Component unmounted");
            
            apiCallCountRef.current++;
            const urlWithCacheBuster = `${getApiBaseUrl()}/api/picture/next?roomId=${localRoomId}&_=${new Date().getTime()}&idx=${index}`;
            try {
                const response = await apiClient.get<{ ret: string, msg: string, image: string }>(urlWithCacheBuster, { timeout: 30000 });
                if (response.data?.ret === 'true' && response.data.image) {
                    return `data:image/png;base64,${response.data.image}`;
                }
                throw new Error(response.data.msg || "API returned no image");
            } catch (err) {
                console.error(`Index ${index} の取得に失敗しました`, err);
                return fallbackImage;
            }
        };

        const runHeartAnimationCycle = async () => {
            console.log(`ハートアニメーション用の画像${HEART_IMAGE_COUNT}枚を0.3秒間隔で取得開始します。`);
            setIsFetching(true);

            const currentFetchedImages = new Array(HEART_IMAGE_COUNT).fill(null);
            let completedCount = 0;

            const allFetchedPromise = new Promise<string[]>((resolve) => {
                for (let i = 0; i < HEART_IMAGE_COUNT; i++) {
                    const triggerFetch = async () => {
                        if (!isMounted.current) return;
                        
                        try {
                            const image = await fetchImageOrThrow(i);
                            if (!isMounted.current) return;

                            currentFetchedImages[i] = image;
                            setHeartImages(prev => {
                                const next = [...prev];
                                next[i] = image;
                                return next;
                            });
                        } catch (err) {
                            console.error(`Index ${i} の取得に失敗しました`, err);
                        } finally {
                            completedCount++;
                            if (completedCount === HEART_IMAGE_COUNT) {
                                resolve(currentFetchedImages);
                            }
                        }
                    };

                    setTimeout(triggerFetch, i * 100);
                }
            });

            const fetchedImages = await allFetchedPromise;

            if (!isMounted.current) return;

            fetchedImagesRef.current = fetchedImages;
            setIsFetching(false);
            console.log("全ての画像の取得処理（または代替処理）が完了しました。");

            console.log("全てのハートが出揃うのを待機しています...");
            await wait(HEART_FADE_DURATION_MS);

            if (!isMounted.current) return;

            console.log(`${(FADEOUT_START_DELAY_MS / 1000).toFixed(2)}秒間、画像を表示します。`);
            await wait(FADEOUT_START_DELAY_MS);

            if (!isMounted.current) return;

            console.log("フェードアウト＆画像合成処理を開始します。");
            setStartFadeOut(true);

            await wait(HEART_FADE_DURATION_MS + 100);

            if (!isMounted.current) return;
            console.log("画像合成処理を開始します。");
            setAnimationComplete(true);

            const lastImageSrc = fetchedImagesRef.current[fetchedImagesRef.current.length - 1];
            if (lastImageSrc) {
                createCompositeImage(lastImageSrc, heartMaskForComposite);
            }
        };

        runHeartAnimationCycle();

    }, [currentScreen, localRoomId, createCompositeImage]);

    // --- レンダリング ---

    if (!localRoomId) {
        return (
            <BackgroundContainer>
                <LoadingOverlay>
                    <p>ルーム情報が見つかりません。正しいページからアクセスしてください。</p>
                </LoadingOverlay>
            </BackgroundContainer>
        );
    }
    
    return (
        <BackgroundContainer>
            {currentScreen === 'background' && (
                <>
                    {isLoading && (
                        <LoadingOverlay>
                            <div className="loading-spinner"></div>
                            <p>画像を読み込んでいます...</p>
                        </LoadingOverlay>
                    )}
                    {!isLoading && imageUrl && (
                        <ImageContainer $isFadingOut={isFadingOut} key={imageUrl}>
                            <img src={imageUrl} alt="Display" />
                        </ImageContainer>
                    )}
                </>
            )}

            {currentScreen === 'heartAnimation' && (
                <>
                    {animationComplete && compositeImage ? (
                        <CompositeImage src={compositeImage} alt="Heart Shaped Composite" />
                    ) : (
                        <FrameWrapper>
                            <HeartContainer>
                                {heartImages.map((src, index) => {
                                    if (!src) return null;
                                    const position = heartPositions[index];
                                    if (!position) return null;
                                    const delay = index * (HEART_ANIMATION_DELAY_MS / 1000);
                                    return (
                                        <HeartImageWrapper key={index} $x={position.x} $y={position.y} $scale={position.scale}>
                                            <HeartImage src={src} alt={`Heart part ${index + 1}`} $delay={delay} $fadeOut={startFadeOut} />
                                        </HeartImageWrapper>
                                    );
                                })}
                            </HeartContainer>
                            <HeartOverlay src={heartOutline} alt="Heart Frame Overlay" $fadeOut={startFadeOut} />
                        </FrameWrapper>
                    )}
                </>
            )}
        </BackgroundContainer>
    );
};

export default HeartAnimationScreen;
